import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Database, Clock, FileBarChart, BarChart3, TrendingUp, Eye, Trash2, Loader2, RotateCcw, Target, Brain, Sparkles } from 'lucide-react';
import { formatImportTimestamp } from '@/hooks/use-import-persistence';
import { Auth } from '@/components/Auth';
import { ThemeToggle } from '@/components/ThemeToggle';
import { AutoSaveIndicator } from '@/components/AutoSaveIndicator';
import { SavedDataMenu } from '@/components/SavedDataMenu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

interface SavedImport {
  id: string;
  imported_at: string;
  updated_at: string;
  type: 'ibkr' | 'sma' | 'sector' | 'outlook' | 'summary';
  summary: string;
  data: any;
}

const CollectedData = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [imports, setImports] = useState<SavedImport[]>([]);
  const [selectedImport, setSelectedImport] = useState<SavedImport | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('all');

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      loadAllImports();
    }
  }, [user]);

  const loadAllImports = async () => {
    setLoading(true);
    try {
      const allImports: SavedImport[] = [];

      // Load IBKR imports
      const { data: ibkrData, error: ibkrError } = await supabase
        .from('ibkr_imports')
        .select('*')
        .order('imported_at', { ascending: false });

      if (!ibkrError && ibkrData) {
        ibkrData.forEach(item => {
          allImports.push({
            id: item.id,
            imported_at: item.imported_at,
            updated_at: item.updated_at,
            type: 'ibkr',
            summary: `Account: ${item.account_id || 'N/A'} | Period: ${item.statement_period || 'N/A'}`,
            data: item
          });
        });
      }


      // Load SMA Surveillance imports
      const { data: smaData, error: smaError } = await supabase
        .from('sma_surveillance_imports')
        .select('*')
        .order('imported_at', { ascending: false });

      if (!smaError && smaData) {
        smaData.forEach(item => {
          allImports.push({
            id: item.id,
            imported_at: item.imported_at,
            updated_at: item.updated_at,
            type: 'sma',
            summary: `${item.total_stocks || 0} stocks | Buy: ${item.buy_signals || 0} | Sell: ${item.sell_signals || 0}`,
            data: item
          });
        });
      }

      // Load Sector Analyses
      const { data: sectorData, error: sectorError } = await supabase
        .from('sector_analyses')
        .select('*')
        .order('created_at', { ascending: false });

      if (!sectorError && sectorData) {
        sectorData.forEach(item => {
          const sectors = Array.isArray(item.sector_data) ? item.sector_data : [];
          const recs = Array.isArray(item.recommendations) ? item.recommendations : [];
          allImports.push({
            id: item.id,
            imported_at: item.created_at,
            updated_at: item.created_at,
            type: 'sector',
            summary: `${sectors.length} sectors | ${recs.length} buy recommendations`,
            data: item
          });
        });
      }

      // Load Market Outlooks
      const { data: outlookData, error: outlookError } = await supabase
        .from('market_outlooks')
        .select('*')
        .order('created_at', { ascending: false });

      if (!outlookError && outlookData) {
        outlookData.forEach((item: any) => {
          const models = Array.isArray(item.outlooks_data) ? item.outlooks_data : [];
          allImports.push({
            id: item.id,
            imported_at: item.created_at,
            updated_at: item.created_at,
            type: 'outlook',
            summary: `${item.models_count || models.length} AI models analysed`,
            data: item
          });
        });
      }

      // Sort all imports by date
      allImports.sort((a, b) => new Date(b.imported_at).getTime() - new Date(a.imported_at).getTime());
      
      setImports(allImports);
    } catch (error) {
      console.error('Error loading imports:', error);
      toast({
        title: "Error loading data",
        description: "Failed to load saved imports",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewImport = (item: SavedImport) => {
    setSelectedImport(item);
    setDialogOpen(true);
  };

  const handleRestoreImport = (item: SavedImport) => {
    const routeMap: Record<string, string> = {
      ibkr: '/ibkr-import',
      sma: '/sma-surveillance',
      sector: '/hedge-protection',
      outlook: '/hedge-protection',
      summary: '/content-summary',
    };

    const state: any = { autoLoad: true };
    if (item.type === 'sector') {
      state.sectorRestoreId = item.id;
    }
    if (item.type === 'outlook') {
      state.outlookRestoreId = item.id;
    }

    navigate(routeMap[item.type], { state });
    
    toast({
      title: "Navigating to analysis",
      description: `Loading your saved ${getTypeLabel(item.type)} data...`
    });
  };

  const handleDeleteImport = async (item: SavedImport) => {
    const tableMap: Record<string, string> = {
      ibkr: 'ibkr_imports',
      sma: 'sma_surveillance_imports',
      sector: 'sector_analyses',
      outlook: 'market_outlooks',
      summary: 'content_summaries',
    };

    const { error } = await supabase
      .from(tableMap[item.type] as any)
      .delete()
      .eq('id', item.id);

    if (error) {
      toast({
        title: "Delete failed",
        description: error.message,
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Deleted",
      description: "Import data has been removed"
    });
    
    loadAllImports();
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'ibkr':
        return <TrendingUp className="h-4 w-4" />;
      case 'sma':
        return <BarChart3 className="h-4 w-4" />;
      case 'sector':
        return <Target className="h-4 w-4" />;
      case 'outlook':
        return <Brain className="h-4 w-4" />;
      case 'summary':
        return <Sparkles className="h-4 w-4" />;
      default:
        return <Database className="h-4 w-4" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'ibkr':
        return 'IBKR Full Analysis';
      case 'sma':
        return 'IBKR Surveillance';
      case 'sector':
        return 'Sector Analysis';
      case 'outlook':
        return 'Market Outlook';
      case 'summary':
        return 'Content Summary';
      default:
        return type;
    }
  };

  const getTypeBadgeVariant = (type: string): "default" | "secondary" | "outline" => {
    switch (type) {
      case 'ibkr':
        return 'default';
      case 'sma':
        return 'outline';
      case 'sector':
        return 'secondary';
      case 'outlook':
        return 'outline';
      case 'summary':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const filteredImports = activeTab === 'all' 
    ? imports 
    : imports.filter(i => i.type === activeTab);

  const renderDataPreview = (item: SavedImport) => {
    const data = item.data;
    
    if (item.type === 'ibkr') {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Account ID</p>
              <p className="font-medium">{data.account_id || 'N/A'}</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Statement Period</p>
              <p className="font-medium">{data.statement_period || 'N/A'}</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Cash Balance</p>
              <p className="font-medium">{data.cash_balance?.toLocaleString() || '0'} {data.currency || ''}</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Realized P/L</p>
              <p className="font-medium">{data.realized_pl?.toLocaleString() || '0'}</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Total Dividends</p>
              <p className="font-medium">{data.total_dividends?.toLocaleString() || '0'}</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Total Fees</p>
              <p className="font-medium">{data.total_fees?.toLocaleString() || '0'}</p>
            </div>
          </div>
          
          {data.positions_data && Array.isArray(data.positions_data) && data.positions_data.length > 0 && (
            <div>
              <h4 className="font-medium mb-2">Positions ({data.positions_data.length})</h4>
              <ScrollArea className="h-[200px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Symbol</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Cost Basis</TableHead>
                      <TableHead>Market Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.positions_data.slice(0, 20).map((pos: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{pos.symbol || pos.ticker}</TableCell>
                        <TableCell>{pos.quantity}</TableCell>
                        <TableCell>{pos.costBasis?.toLocaleString() || pos.cost_basis?.toLocaleString()}</TableCell>
                        <TableCell>{pos.marketValue?.toLocaleString() || pos.market_value?.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          )}
        </div>
      );
    }

    if (item.type === 'sma') {
      const results = data.analysis_results as
        | {
            smaResults?: any[];
            dailyIchimokuResults?: any[];
            weeklyIchimokuResults?: any[];
            smaSummary?: any;
            dailyIchimokuSummary?: any;
            weeklyIchimokuSummary?: any;
          }
        | null
        | undefined;

      const smaResults = results?.smaResults ?? [];
      const dailyIchimokuResults = results?.dailyIchimokuResults ?? [];
      const weeklyIchimokuResults = results?.weeklyIchimokuResults ?? [];

      return (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Total Stocks</p>
              <p className="font-medium">{data.total_stocks || 0}</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">With Signals</p>
              <p className="font-medium">{data.stocks_with_signals || 0}</p>
            </div>
            <div className="p-3 bg-success/10 rounded-lg">
              <p className="text-sm text-muted-foreground">Buy Signals</p>
              <p className="font-medium text-success">{data.buy_signals || 0}</p>
            </div>
            <div className="p-3 bg-destructive/10 rounded-lg">
              <p className="text-sm text-muted-foreground">Sell Signals</p>
              <p className="font-medium text-destructive">{data.sell_signals || 0}</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">SMA rows</p>
              <p className="font-medium">{smaResults.length}</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Ichimoku (daily)</p>
              <p className="font-medium">{dailyIchimokuResults.length}</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Ichimoku (weekly)</p>
              <p className="font-medium">{weeklyIchimokuResults.length}</p>
            </div>
          </div>

          {smaResults.length > 0 && (
            <div>
              <h4 className="font-medium mb-2">SMA Snapshot (first 20)</h4>
              <ScrollArea className="h-[200px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ticker</TableHead>
                      <TableHead>Signal</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">SMA 50</TableHead>
                      <TableHead className="text-right">SMA 200</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {smaResults.slice(0, 20).map((result: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{result.ticker}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              result.signal50_200 === 'BUY' || result.signal2_12 === 'BUY'
                                ? 'default'
                                : result.signal50_200 === 'SELL' || result.signal2_12 === 'SELL'
                                  ? 'destructive'
                                  : 'secondary'
                            }
                          >
                            {result.signal50_200 || result.signal2_12 || 'NEUTRAL'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{result.currentPrice?.toFixed?.(2) ?? '-'}</TableCell>
                        <TableCell className="text-right">{result.sma50?.toFixed?.(2) ?? '-'}</TableCell>
                        <TableCell className="text-right">{result.sma200?.toFixed?.(2) ?? '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
              <p className="text-xs text-muted-foreground mt-2">
                Note: charts are fetched on-demand after restore to keep saves fast.
              </p>
            </div>
          )}
        </div>
      );
    }

    if (item.type === 'sector') {
      const sectors = Array.isArray(data.sector_data) ? data.sector_data : [];
      const recs = Array.isArray(data.recommendations) ? data.recommendations : [];
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Sectors Analysed</p>
              <p className="font-medium">{sectors.length}</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Buy Recommendations</p>
              <p className="font-medium">{recs.length} sectors</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Fetched At</p>
              <p className="font-medium text-xs">{data.fetched_at ? new Date(data.fetched_at).toLocaleString() : 'N/A'}</p>
            </div>
          </div>
          {sectors.length > 0 && (
            <div>
              <h4 className="font-medium mb-2">Sector Performance</h4>
              <ScrollArea className="h-[200px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sector</TableHead>
                      <TableHead>Symbol</TableHead>
                      <TableHead className="text-right">1D</TableHead>
                      <TableHead className="text-right">1W</TableHead>
                      <TableHead className="text-right">1M</TableHead>
                      <TableHead className="text-right">1Y</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sectors.map((s: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{s.sector}</TableCell>
                        <TableCell>{s.symbol}</TableCell>
                        <TableCell className={`text-right ${s.performance?.day1 >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                          {s.performance?.day1?.toFixed(1)}%
                        </TableCell>
                        <TableCell className={`text-right ${s.performance?.week1 >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                          {s.performance?.week1?.toFixed(1)}%
                        </TableCell>
                        <TableCell className={`text-right ${s.performance?.month1 >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                          {s.performance?.month1?.toFixed(1)}%
                        </TableCell>
                        <TableCell className={`text-right ${s.performance?.year1 >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                          {s.performance?.year1?.toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          )}
        </div>
      );
    }

    if (item.type === 'outlook') {
      const models = Array.isArray(data.outlooks_data) ? data.outlooks_data : [];
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">AI Models</p>
              <p className="font-medium">{data.models_count || models.length}</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Generated At</p>
              <p className="font-medium text-xs">{data.created_at ? new Date(data.created_at).toLocaleString() : 'N/A'}</p>
            </div>
          </div>
          {models.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-medium">Model Analyses</h4>
              {models.map((m: any, idx: number) => (
                <div key={idx} className="p-3 rounded-lg border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Brain className="h-4 w-4 text-purple-500" />
                    <p className="text-sm font-semibold">{m.label}</p>
                  </div>
                  <ScrollArea className="max-h-[150px]">
                    <p className="text-xs text-muted-foreground whitespace-pre-wrap">{m.content?.slice(0, 500)}{m.content?.length > 500 ? '...' : ''}</p>
                  </ScrollArea>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (item.type === 'summary') {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Video URL</p>
              <a href={data.video_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline break-all">{data.video_url}</a>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Saved At</p>
              <p className="font-medium text-xs">{data.created_at ? new Date(data.created_at).toLocaleString() : 'N/A'}</p>
            </div>
          </div>
          {data.video_id && (
            <img src={`https://img.youtube.com/vi/${data.video_id}/mqdefault.jpg`} alt="Thumbnail" className="w-48 rounded-lg" />
          )}
          <ScrollArea className="max-h-[300px]">
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <ReactMarkdown>{data.summary_content}</ReactMarkdown>
            </div>
          </ScrollArea>
        </div>
      );
    }

    return <pre className="text-xs overflow-auto max-h-[300px]">{JSON.stringify(data, null, 2)}</pre>;
  };

  if (!user) {
    return <Auth />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
      {/* Decorative background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-accent/5 rounded-full blur-3xl" />
      </div>

      <div className="relative container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-primary to-primary/80 rounded-2xl shadow-lg">
                <Database className="h-6 w-6 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Collected Data</h1>
                <p className="text-sm text-muted-foreground">View and manage your saved analyses</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <SavedDataMenu />
            <AutoSaveIndicator />
            <ThemeToggle />
          </div>
        </div>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="all" className="gap-2">
              <Database className="h-4 w-4" />
              All ({imports.length})
            </TabsTrigger>
            <TabsTrigger value="ibkr" className="gap-2">
              <TrendingUp className="h-4 w-4" />
              IBKR ({imports.filter(i => i.type === 'ibkr').length})
            </TabsTrigger>
            <TabsTrigger value="sma" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Surveillance ({imports.filter(i => i.type === 'sma').length})
            </TabsTrigger>
            <TabsTrigger value="sector" className="gap-2">
              <Target className="h-4 w-4" />
              Sector ({imports.filter(i => i.type === 'sector').length})
            </TabsTrigger>
            <TabsTrigger value="outlook" className="gap-2">
              <Brain className="h-4 w-4" />
              Outlook ({imports.filter(i => i.type === 'outlook').length})
            </TabsTrigger>
            <TabsTrigger value="summary" className="gap-2">
              <Sparkles className="h-4 w-4" />
              Summaries ({imports.filter(i => i.type === 'summary').length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredImports.length === 0 ? (
              <Card className="bg-card/90 backdrop-blur-sm">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Database className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">No saved data</h3>
                  <p className="text-muted-foreground text-center max-w-md">
                    Run an analysis from the dashboard to save data here. Your IBKR and Surveillance analyses will appear in this list.
                  </p>
                  <Button className="mt-6" onClick={() => navigate('/')}>
                    Go to Dashboard
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredImports.map(item => (
                  <Card key={`${item.type}-${item.id}`} className="bg-card/90 backdrop-blur-sm hover:shadow-lg transition-all">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          <div className={`p-3 rounded-xl ${
                            item.type === 'ibkr' ? 'bg-primary/10' :
                            item.type === 'sector' ? 'bg-amber-500/10' :
                            item.type === 'outlook' ? 'bg-purple-500/10' :
                            'bg-purple-500/10'
                          }`}>
                            {getTypeIcon(item.type)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant={getTypeBadgeVariant(item.type)}>
                                {getTypeLabel(item.type)}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{item.summary}</p>
                            <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              <span>Saved: {formatImportTimestamp(item.imported_at)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="default" 
                            size="sm" 
                            onClick={() => handleRestoreImport(item)}
                            className="bg-primary hover:bg-primary/90"
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Restore
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleViewImport(item)}>
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete this import?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete the saved {getTypeLabel(item.type)} data from {formatImportTimestamp(item.imported_at)}. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDeleteImport(item)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* View Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedImport && getTypeIcon(selectedImport.type)}
                {selectedImport && getTypeLabel(selectedImport.type)}
              </DialogTitle>
              <DialogDescription>
                {selectedImport && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Saved: {formatImportTimestamp(selectedImport.imported_at)}
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            {selectedImport && renderDataPreview(selectedImport)}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CollectedData;
